import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import IndexHero from "@/components/home/IndexHero";
import PopularPrompts from "@/components/home/PopularPrompts";
import ValuePropositionSection from "@/components/home/ValuePropositionSection";
import ProductCopilotPreview from "@/components/home/ProductCopilotPreview";
import PromptSavingDemo from "@/components/home/PromptSavingDemo";
import PremiumFeatureTeaser from "@/components/home/PremiumFeatureTeaser";
import FeatureSection from "@/components/home/FeatureSection";
import TestimonialsSection from "@/components/home/TestimonialsSection";
import CtaSection from "@/components/home/CtaSection";
import { usePopularPrompts } from "@/hooks/usePopularPrompts";
import { useCategories } from "@/hooks/useCategories";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const features = [
  {
    title: "Get Results 10x Faster",
    description: "Expert-crafted prompts that deliver professional results in minutes, not hours. Skip the trial and error."
  },
  {
    title: "Product Copilot AI Assistant",
    description: "Your personal AI assistant for creating PRDs, project specs, and strategic documents with premium templates."
  },
  {
    title: "Save & Organize Your Arsenal",
    description: "Build your personal prompt library with unlimited saves, custom folders, and instant search across all your prompts."
  },
  {
    title: "Proven by 10,000+ Users",
    description: "Join professionals who've transformed their workflows with our curated collection of high-performing prompts."
  }
];

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories, isLoading: isLoadingCategories } = useCategories();
  const { 
    prompts: featuredPrompts, 
    isLoading: isLoadingPrompts, 
    error: promptsError,
    refetch: refetchPrompts
  } = usePopularPrompts(8);

  const handleSearch = (query: string) => {
    navigate(`/explore?q=${encodeURIComponent(query)}`);
  };
  
  useEffect(() => {
    console.log("Featured prompts changed:", { 
      count: featuredPrompts?.length || 0, 
      isLoading: isLoadingPrompts,
      hasError: !!promptsError
    });
    
    if (promptsError) {
      console.error("Error loading featured prompts:", promptsError);
    }
  }, [featuredPrompts, isLoadingPrompts, promptsError]);

  console.log("Index page rendering with prompts:", 
    featuredPrompts ? `${featuredPrompts.length} prompts` : 'no prompts', 
    "loading:", isLoadingPrompts
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <IndexHero onSearch={handleSearch} />
      <PopularPrompts 
        prompts={featuredPrompts || []} 
        isLoading={isLoadingPrompts} 
        error={promptsError}
        refetch={refetchPrompts}
      />
      <ValuePropositionSection />
      <ProductCopilotPreview />
      <PromptSavingDemo />
      <PremiumFeatureTeaser />
      <FeatureSection features={features} />
      <TestimonialsSection />
      <CtaSection />
      <Footer />
    </div>
  );
};

export default Index;
